/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAreaTagsOptimistic } from '@/features/area-tags/hooks/useAreaTags';
import {
  getLayouts,
  getAreaTags,
  getPhotoMarkers,
  getAvailablePhotos,
  getPhoto360,
  getPhotoHotspots,
  getHotspotOverlays,
  createHotspot,
  createNote,
  updateNote,
  deleteNote,
  getNotes,
  createNoteAttachment,
  getProjectUsers,
  getNote,
  uploadOverlay,
  deleteOverlay,
  deleteHotspot,
  updateOverlay,
  createAreaTag,
  savePhotoMarkers,
  createLabel,
  uploadOverlayToHotspot,
  updateHotspot,
  getHotspotOverlayGroups,
  createHotspotOverlayGroup,
} from '../api/api';
import {
  Layout,
  Unit,
  PhotoMarker,
  Photo,
  Photo360Response,
  PhotoHotspotsResponse,
  HotspotOverlaysResponse,
  ViewPosition,
  CreateHotspotResponse,
  Note,
  ProjectUser,
  UploadOverlayResponse,
  CreateAreaTagData,
  SaveMarkersData,
  CreateAreaTagResponse,
  SaveMarkersResponse,
  Label,
  HotspotOverlay,
  HotspotOverlayGroupsResponse,
  HotspotOverlayGroup,
} from '../types/types';
import { findNearbyHotspots, toSimplePosition } from '../utils/hotspotUtils';
import { invalidateLayoutHotspots } from './useLayoutHotspots';

export const useLayouts = (projectId: string | undefined, type?: string) => {
  return useQuery<Layout[]>({
    queryKey: ['layouts', projectId, type],
    queryFn: () => getLayouts(projectId!, type),
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes - reasonable cache time with manual invalidation
  });
};

// Legacy hook - now using optimistic area tags from area-tags feature
// Keeping for backward compatibility but deprecated
export const useAreaTags = (layoutId: string | undefined) => {
  return useQuery<Unit[]>({
    queryKey: ['areaTags', layoutId],
    queryFn: () => getAreaTags(layoutId!),
    enabled: !!layoutId,
    staleTime: 1000 * 60 * 60 * 24,
  });
};

export const usePhotoMarkers = (layoutId: string | undefined) => {
  return useQuery<PhotoMarker[]>({
    queryKey: ['photoMarkers', layoutId],
    queryFn: () => getPhotoMarkers(layoutId!),
    enabled: !!layoutId,
    staleTime: 1000 * 60 * 60 * 24,
  });
};

export const useAvailablePhotos = (layoutId: string | undefined) => {
  return useQuery<Photo[]>({
    queryKey: ['availablePhotos', layoutId],
    queryFn: () => getAvailablePhotos(layoutId!),
    enabled: !!layoutId,
    staleTime: 1000 * 60 * 60 * 24,
  });
};

// Deprecated: useShowPhoto hook removed - use usePhoto360 instead

export const usePhoto360 = (photoId: number | undefined) => {
  return useQuery<Photo360Response>({
    queryKey: ['photo360', photoId],
    queryFn: () => getPhoto360(photoId!),
    enabled: photoId !== undefined,
  });
};

export const usePhotoHotspots = (photoId: number | undefined) => {
  return useQuery<PhotoHotspotsResponse>({
    queryKey: ['photoHotspots', photoId],
    queryFn: () => getPhotoHotspots(photoId!),
    enabled: photoId !== undefined,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useHotspotOverlays = (hotspotId: number | undefined) => {
  return useQuery<HotspotOverlaysResponse>({
    queryKey: ['hotspotOverlays', hotspotId],
    queryFn: () => getHotspotOverlays(hotspotId!),
    enabled: hotspotId !== undefined,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  });
};

export const useHotspotOverlayGroups = (hotspotId: number | undefined) => {
  return useQuery<HotspotOverlayGroupsResponse>({
    queryKey: ['hotspotOverlayGroups', hotspotId],
    queryFn: () => getHotspotOverlayGroups(hotspotId!),
    enabled: hotspotId !== undefined,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  });
};

export const useCreateHotspotOverlayGroup = () => {
  const queryClient = useQueryClient();

  return useMutation<
    HotspotOverlayGroup,
    Error,
    {
      hotspotId: number;
      name?: string;
    },
    { previousGroups?: HotspotOverlayGroupsResponse }
  >({
    mutationFn: ({ hotspotId, name }) => createHotspotOverlayGroup(hotspotId, { name }),
    onMutate: async ({ hotspotId, name }) => {
      await queryClient.cancelQueries({ queryKey: ['hotspotOverlayGroups', hotspotId] });
      const previousGroups = queryClient.getQueryData<HotspotOverlayGroupsResponse>([
        'hotspotOverlayGroups',
        hotspotId,
      ]);

      const optimisticGroup: HotspotOverlayGroup = {
        id: Date.now(),
        hotspot: hotspotId,
        name: name || null,
        overlay_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      queryClient.setQueryData(
        ['hotspotOverlayGroups', hotspotId],
        (old: HotspotOverlayGroupsResponse | undefined) => ({
          groups: [...(old?.groups || []), optimisticGroup],
        })
      );

      return { previousGroups };
    },
    onError: (err, { hotspotId }, context) => {
      console.error('Error creating overlay group:', err);
      queryClient.setQueryData(['hotspotOverlayGroups', hotspotId], context?.previousGroups);
    },
    onSettled: (_data, _error, { hotspotId }) => {
      queryClient.invalidateQueries({ queryKey: ['hotspotOverlayGroups', hotspotId] });
      queryClient.invalidateQueries({ queryKey: ['hotspotOverlays', hotspotId] });
      queryClient.invalidateQueries({ queryKey: ['hotspotObjects', hotspotId] });
      invalidateLayoutHotspots(queryClient);
    },
  });
};

export const useCreateHotspot = () => {
  const queryClient = useQueryClient();

  return useMutation<
    CreateHotspotResponse,
    Error,
    {
      photoId: number;
      position: ViewPosition;
    }
  >({
    mutationFn: ({ photoId, position }) => {
      return createHotspot(photoId, position);
    },
    onMutate: async ({ photoId, position }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['photoHotspots', photoId] });

      // Snapshot previous value
      const previousHotspots = queryClient.getQueryData(['photoHotspots', photoId]);

      // Optimistically update the cache
      const optimisticHotspot = {
        id: Date.now(),
        pitch: position.pitch,
        yaw: position.yaw,
        hfov: position.hfov,
        overlay_count: 0,
        created_by: 'current_user',
        created_at: new Date().toISOString(),
      };

      queryClient.setQueryData(
        ['photoHotspots', photoId],
        (old: PhotoHotspotsResponse | undefined) => {
          const newValue = old 
            ? { hotspots: [...old.hotspots, optimisticHotspot] }
            : { hotspots: [optimisticHotspot] };
          return newValue;
        }
      );

      return { previousHotspots };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      console.error('Error creating hotspot:', err);
      const typedContext = context as { previousHotspots?: PhotoHotspotsResponse } | undefined;
      if (typedContext?.previousHotspots) {
        queryClient.setQueryData(
          ['photoHotspots', variables.photoId],
          typedContext.previousHotspots
        );
      }
    },
    onSettled: async (_data, error, variables) => {
      if (error) {
        console.error('Error creating hotspot:', error);
      }
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['photoHotspots', variables.photoId] });
      // Also invalidate layout hotspots for immediate reflection in hotspots table
      invalidateLayoutHotspots(queryClient);
    },
  });
};

export const useCreateNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (noteData: {
      hotspot: number;
      title: string;
      subject: string;
      description: string;
      reminder_date?: string | null;
      reminder_time?: string | null;
      reminder_message?: string;
      visible_to?: string[];
    }) => createNote(noteData),
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['notes', variables.hotspot] });
      queryClient.invalidateQueries({ queryKey: ['hotspotObjects', variables.hotspot] });

      // Invalidate layout hotspots for immediate reflection in hotspots table
      invalidateLayoutHotspots(queryClient);
    },
  });
};

export const useUpdateNote = (projectId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      noteId,
      noteData,
    }: {
      noteId: number;
      noteData: {
        title: string;
        subject: string;
        description: string;
        reminder_date?: string | null;
        reminder_time?: string | null;
        reminder_message?: string;
        visible_to?: string[];
      };
    }) => {
      if (!projectId) {
        throw new Error('Project ID is required for updating notes');
      }
      return updateNote(noteId, noteData, projectId);
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['note', variables.noteId], data);
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      queryClient.invalidateQueries({ queryKey: ['note', variables.noteId] });
      queryClient.invalidateQueries({ queryKey: ['hotspotObjects'] });

      // Invalidate layout hotspots for immediate reflection in hotspots table
      invalidateLayoutHotspots(queryClient);
    },
    onError: (error) => {
      console.error('Error updating note:', error);
      queryClient.invalidateQueries({ queryKey: ['notes'] });

      // Also invalidate layout hotspots on error to ensure consistency
      invalidateLayoutHotspots(queryClient);
    },
  });
};

export const useUpdateHotspot = (projectId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      hotspotId,
      data,
    }: {
      hotspotId: number;
      data: {
        title?: string;
        pitch?: number;
        yaw?: number;
        hfov?: number;
      };
    }) => {
      if (!projectId) {
        throw new Error('Project ID is required for updating hotspots');
      }
      return updateHotspot(hotspotId, data, projectId);
    },
    onMutate: async ({ hotspotId, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['photoHotspots'] });
      await queryClient.cancelQueries({ queryKey: ['hotspotObjects', hotspotId] });
      await queryClient.cancelQueries({ queryKey: ['layoutHotspots'] });

      // Snapshot previous values
      const previousPhotoHotspots = queryClient.getQueriesData({ queryKey: ['photoHotspots'] });
      const previousHotspotObjects = queryClient.getQueryData(['hotspotObjects', hotspotId]);
      const previousLayoutHotspots = queryClient.getQueriesData({ queryKey: ['layoutHotspots'] });

      // Optimistically update photoHotspots cache
      queryClient.setQueriesData(
        { queryKey: ['photoHotspots'] },
        (old: PhotoHotspotsResponse | undefined) => {
          if (!old?.hotspots) return old;
          return {
            ...old,
            hotspots: old.hotspots.map((hotspot) =>
              hotspot.id === hotspotId ? { ...hotspot, ...data } : hotspot
            ),
          };
        }
      );

      // Optimistically update layoutHotspots cache (this is likely where the dialog gets hotspot data)
      queryClient.setQueriesData({ queryKey: ['layoutHotspots'] }, (old: unknown) => {
        if (!old || typeof old !== 'object' || !('hotspots' in old)) return old;
        const oldData = old as { hotspots: Array<{ id: number; [key: string]: unknown }> };
        return {
          ...oldData,
          hotspots: oldData.hotspots.map((hotspot) =>
            hotspot.id === hotspotId ? { ...hotspot, ...data } : hotspot
          ),
        };
      });

      return { previousPhotoHotspots, previousHotspotObjects, previousLayoutHotspots };
    },
    onError: (err, variables, context) => {
      console.error('Error updating hotspot:', err);

      // Rollback optimistic updates on error
      if (context?.previousPhotoHotspots) {
        context.previousPhotoHotspots.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousHotspotObjects) {
        queryClient.setQueryData(
          ['hotspotObjects', variables.hotspotId],
          context.previousHotspotObjects
        );
      }
      if (context?.previousLayoutHotspots) {
        context.previousLayoutHotspots.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: (data, variables) => {
      // Update specific hotspot data
      queryClient.setQueriesData(
        { queryKey: ['photoHotspots'] },
        (old: PhotoHotspotsResponse | undefined) => {
          if (!old?.hotspots) return old;
          return {
            ...old,
            hotspots: old.hotspots.map((hotspot) =>
              hotspot.id === variables.hotspotId ? { ...hotspot, ...data } : hotspot
            ),
          };
        }
      );

      // Invalidate layout hotspots for immediate reflection in hotspots table
      invalidateLayoutHotspots(queryClient);
    },
    onSettled: (_, error, variables) => {
      if (error) {
        console.error('Error updating hotspot:', error);
        // Invalidate all related queries on error to ensure consistency
        queryClient.invalidateQueries({ queryKey: ['photoHotspots'] });
        queryClient.invalidateQueries({ queryKey: ['hotspotObjects', variables.hotspotId] });
        invalidateLayoutHotspots(queryClient);
      }
    },
  });
};

export const useGetNotes = (hotspotId: number | undefined) => {
  return useQuery<Note[]>({
    queryKey: ['notes', hotspotId],
    queryFn: () => getNotes(hotspotId!),
    enabled: hotspotId !== undefined,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  });
};

export const useCreateNoteAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { noteId: number; file: File; hotspotId: number }) =>
      createNoteAttachment(params.noteId, params.file),
    onMutate: async ({ noteId, file, hotspotId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['notes', hotspotId] });
      await queryClient.cancelQueries({ queryKey: ['note', noteId] });

      // Snapshot previous values
      const previousNotes = queryClient.getQueryData(['notes', hotspotId]);
      const previousNote = queryClient.getQueryData(['note', noteId]);

      // Create optimistic attachment
      const optimisticAttachment = {
        id: Date.now(), // Temporary ID
        note: noteId,
        gcs_url: '',
        original_filename: file.name,
        file_size: file.size,
        content_type: file.type,
        uploaded_by: 'Uploading...',
        uploaded_at: new Date().toISOString(),
        download_url: '#', // Temporary URL
      };

      // Optimistically update the notes cache (used by useHotspotObjects)
      queryClient.setQueryData(['notes', hotspotId], (old: Note[] | undefined) => {
        if (!old) return old;
        return old.map((note) => {
          if (note.id === noteId) {
            return {
              ...note,
              attachments: [...(note.attachments || []), optimisticAttachment],
            };
          }
          return note;
        });
      });

      // Also update individual note cache if it exists
      queryClient.setQueryData(['note', noteId], (old: Note | undefined) => {
        if (!old) return old;
        return {
          ...old,
          attachments: [...(old.attachments || []), optimisticAttachment],
        };
      });

      return { previousNotes, previousNote };
    },
    onError: (err, { noteId, hotspotId }, context) => {
      console.error('Error creating note attachment:', err);
      // Rollback on error
      if (context?.previousNotes) {
        queryClient.setQueryData(['notes', hotspotId], context.previousNotes);
      }
      if (context?.previousNote) {
        queryClient.setQueryData(['note', noteId], context.previousNote);
      }
    },
    onSettled: async (_, error, { noteId, hotspotId }) => {
      if (error) {
        console.error('Error creating note attachment:', error);
      }
      queryClient.invalidateQueries({ queryKey: ['notes', hotspotId] });
      queryClient.invalidateQueries({ queryKey: ['note', noteId] });
      queryClient.invalidateQueries({ queryKey: ['hotspotObjects', hotspotId] });

      // Invalidate layout hotspots for immediate reflection in hotspots table
      invalidateLayoutHotspots(queryClient);
    },
  });
};

export const useProjectUsers = (projectId: string | undefined) => {
  return useQuery<ProjectUser[]>({
    queryKey: ['projectUsers', projectId],
    queryFn: () => getProjectUsers(projectId!),
    enabled: !!projectId,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
  });
};

export const useGetNote = (noteId: number | undefined) => {
  return useQuery<Note>({
    queryKey: ['note', noteId],
    queryFn: () => getNote(noteId!),
    enabled: noteId !== undefined,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  });
};

export const useCreateAreaTag = () => {
  const queryClient = useQueryClient();

  return useMutation<CreateAreaTagResponse, Error, CreateAreaTagData>({
    mutationFn: (data) => createAreaTag(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['areaTags', variables.layout_id] });
    },
  });
};

export const useSavePhotoMarkers = () => {
  const queryClient = useQueryClient();

  return useMutation<SaveMarkersResponse, Error, SaveMarkersData>({
    mutationFn: (data) => savePhotoMarkers(data.layoutId, data.markers, data.markerAttributes),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['photoMarkers', variables.layoutId] });
    },
  });
};

export const useUploadOverlay = () => {
  const queryClient = useQueryClient();

  return useMutation<
    UploadOverlayResponse,
    Error,
    { hotspotId: number; file: File; title?: string; groupId?: number }
  >({
    mutationFn: ({ hotspotId, file, title, groupId }) =>
      uploadOverlay(hotspotId, file, title, groupId),
    onSuccess: async (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hotspotOverlays', variables.hotspotId] });
      queryClient.invalidateQueries({ queryKey: ['hotspotOverlayGroups', variables.hotspotId] });
      queryClient.invalidateQueries({ queryKey: ['hotspotObjects', variables.hotspotId] });

      // Invalidate layout hotspots for immediate reflection in hotspots table
      invalidateLayoutHotspots(queryClient);
    },
  });
};

export const useLayoutData = (layoutId: string | undefined, projectId: string | undefined) => {
  // Use the optimistic area tags hook for immediate cache updates
  const areaTags = useAreaTagsOptimistic(layoutId || '', projectId || '');
  const photoMarkers = usePhotoMarkers(layoutId);
  const availablePhotos = useAvailablePhotos(layoutId);

  // Memoize return value to prevent infinite loops
  return React.useMemo(() => ({
    areaTags: areaTags.data || [],
    photoMarkers: photoMarkers.data || [],
    availablePhotos: availablePhotos.data || [],
    isLoading: areaTags.isInitialLoading || photoMarkers.isLoading || availablePhotos.isLoading,
    error: areaTags.error || photoMarkers.error || availablePhotos.error,
    refetch: () => {
      areaTags.refetch();
      photoMarkers.refetch();
      availablePhotos.refetch();
    },
  }), [areaTags.data, photoMarkers.data, availablePhotos.data, areaTags.isInitialLoading, photoMarkers.isLoading, availablePhotos.isLoading, areaTags.error, photoMarkers.error, availablePhotos.error]);
};

export const usePhotoData = (photoId: number | undefined) => {
  const photo360Query = usePhoto360(photoId);
  const hotspotQuery = usePhotoHotspots(photoId);

  return {
    photoData: photo360Query.data, // Use 360 data as main photo data
    photo360Data: photo360Query.data,
    hotspotsData: hotspotQuery.data,
    isLoading: photo360Query.isLoading || hotspotQuery.isLoading,
    error: photo360Query.error || hotspotQuery.error,
    refetchHotspots: hotspotQuery.refetch,
  };
};

export const useAddPhotoWithHotspotGrouping = () => {
  const queryClient = useQueryClient();
  const createHotspotMutation = useCreateHotspot();
  const uploadOverlayMutation = useUploadOverlay();

  const addPhotoWithHotspotGrouping = async (params: {
    photoId: number;
    position: ViewPosition;
    file: File;
    title?: string;
    groupId?: number;
  }) => {
    try {
      // Get existing hotspots for this photo
      const hotspotResponse = await queryClient.ensureQueryData({
        queryKey: ['photoHotspots', params.photoId],
        queryFn: () => getPhotoHotspots(params.photoId),
      });

      const existingHotspots = hotspotResponse.hotspots.map(
        (h: { id: number; pitch: number; yaw: number }) => ({
          id: h.id,
          pitch: h.pitch,
          yaw: h.yaw,
        })
      );

      // Check for nearby hotspots
      const simplePosition = toSimplePosition(params.position);
      const nearbyHotspotIds = findNearbyHotspots(simplePosition, existingHotspots);

      let hotspotId: number;

      if (nearbyHotspotIds.length > 0) {
        // Use the first nearby hotspot
        hotspotId = nearbyHotspotIds[0];
      } else {
        const hotspot = await createHotspotMutation.mutateAsync({
          photoId: params.photoId,
          position: params.position,
        });
        hotspotId = hotspot.id;
      }

      const overlay = await uploadOverlayMutation.mutateAsync({
        hotspotId: hotspotId,
        file: params.file,
        title: params.title,
        groupId: params.groupId,
      });

      queryClient.invalidateQueries({ queryKey: ['photoHotspots', params.photoId] });

      // Invalidate layout hotspots for immediate reflection in hotspots table
      invalidateLayoutHotspots(queryClient);

      return { hotspotId, overlay };
    } catch (error) {
      console.error('Error creating photo hotspot with grouping:', error);
      throw error;
    }
  };

  return {
    addPhotoWithHotspotGrouping,
    isLoading: createHotspotMutation.isPending || uploadOverlayMutation.isPending,
    error: createHotspotMutation.error || uploadOverlayMutation.error,
  };
};

export const useHotspotObjects = (hotspotId: number | undefined, _projectId: string | undefined) => {
  const overlaysQuery = useHotspotOverlays(hotspotId);

  const allObjects = React.useMemo(() => {
    const objects: Array<{
      id: number | string;
      type: 'note' | 'image' | 'label';
      title: string;
      created_at: string;
      updated_at: string;
      rawData?: Note | HotspotOverlay | Label;
    }> = [];

    // Add images (overlays)
    if (overlaysQuery.data?.overlays) {
      overlaysQuery.data.overlays.forEach((overlay: HotspotOverlay) => {
        const overlayWithDate = overlay as HotspotOverlay & { created_at?: string };
        objects.push({
          id: overlay.id,
          type: 'image',
          title: overlay.title || 'Image',
          created_at: overlayWithDate.created_at || new Date().toISOString(),
          updated_at: overlayWithDate.created_at || new Date().toISOString(),
          rawData: overlay,
        });
      });
    }

    // Sort by creation date (newest first)
    return objects.sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [overlaysQuery.data]);

  return {
    objects: allObjects,
    isLoading: overlaysQuery.isLoading,
    error: overlaysQuery.error,
    refetch: useCallback(() => {
      if (overlaysQuery.isStale) overlaysQuery.refetch();
    }, [overlaysQuery.refetch, overlaysQuery.isStale]),
  };
};

export const useCreateNoteToHotspot = () => {
  const queryClient = useQueryClient();
  const createNoteMutation = useCreateNote();
  const createAttachmentMutation = useCreateNoteAttachment();

  const createNoteToHotspot = async (params: {
    hotspot: number;
    title: string;
    subject: string;
    description: string;
    reminder_date?: string | null;
    reminder_time?: string | null;
    reminder_message?: string;
    visible_to?: string[];
    attachments?: FileList | null;
  }) => {
    try {
      const note = await createNoteMutation.mutateAsync({
        hotspot: params.hotspot,
        title: params.title,
        subject: params.subject,
        description: params.description,
        reminder_date: params.reminder_date,
        reminder_time: params.reminder_time,
        reminder_message: params.reminder_message,
        visible_to: params.visible_to,
      });

      if (params.attachments && params.attachments.length > 0) {
        const attachmentPromises = Array.from(params.attachments).map((file) =>
          createAttachmentMutation.mutateAsync({ noteId: note.id, file, hotspotId: params.hotspot })
        );
        await Promise.all(attachmentPromises);
      }

      queryClient.invalidateQueries({ queryKey: ['notes', params.hotspot] });
      queryClient.invalidateQueries({ queryKey: ['hotspotObjects', params.hotspot] });
      queryClient.invalidateQueries({ queryKey: ['photoHotspots'] });

      // Invalidate layout hotspots for immediate reflection in hotspots table
      invalidateLayoutHotspots(queryClient);

      return note;
    } catch (error) {
      console.error('Error creating note to hotspot:', error);
      throw error;
    }
  };

  return {
    createNoteToHotspot,
    isLoading: createNoteMutation.isPending || createAttachmentMutation.isPending,
    error: createNoteMutation.error || createAttachmentMutation.error,
  };
};

export const useCreateLabelToHotspot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createLabel,
    onMutate: async (newLabel) => {
      await queryClient.cancelQueries({ queryKey: ['labels', newLabel.hotspot] });

      const previousLabels = queryClient.getQueryData(['labels', newLabel.hotspot]);

      const optimisticLabel: Label = {
        id: Date.now(),
        hotspot: newLabel.hotspot,
        title: newLabel.title,
        description: newLabel.description,
        color: newLabel.color,
        created_at: new Date().toISOString(),
      };

      queryClient.setQueryData(['labels', newLabel.hotspot], (old: Label[] | undefined) => {
        return old ? [...old, optimisticLabel] : [optimisticLabel];
      });

      return { previousLabels };
    },
    onError: (err, newLabel, context) => {
      console.error('Error creating label:', err);
      queryClient.setQueryData(['labels', newLabel.hotspot], context?.previousLabels);
    },
    onSettled: async (_, error, variables) => {
      if (error) {
        console.error('Error creating label:', error);
      }
      queryClient.invalidateQueries({ queryKey: ['labels', variables.hotspot] });
      queryClient.invalidateQueries({ queryKey: ['hotspotObjects', variables.hotspot] });
      queryClient.invalidateQueries({ queryKey: ['photoHotspots'] });

      // Invalidate layout hotspots for immediate reflection in hotspots table
      invalidateLayoutHotspots(queryClient);
    },
  });
};

export const useUploadImageToHotspot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      hotspotId,
      file,
      title,
      groupId,
    }: {
      hotspotId: number;
      file: File;
      title?: string;
      groupId?: number;
    }) => uploadOverlayToHotspot(hotspotId, file, title, groupId),
    onMutate: async ({ hotspotId, file }) => {
      await queryClient.cancelQueries({ queryKey: ['hotspotOverlays', hotspotId] });

      const previousOverlays = queryClient.getQueryData(['hotspotOverlays', hotspotId]);

      const optimisticOverlay: HotspotOverlay & { created_at: string } = {
        id: Date.now(),
        url: URL.createObjectURL(file), // Temporary preview URL
        created_at: new Date().toISOString(),
      };

      queryClient.setQueryData(
        ['hotspotOverlays', hotspotId],
        (old: HotspotOverlaysResponse | undefined) => {
          const overlays = old?.overlays || [];
          return { overlays: [...overlays, optimisticOverlay] };
        }
      );

      return { previousOverlays };
    },
    onError: (err, { hotspotId }, context) => {
      console.error('Error uploading image:', err);
      queryClient.setQueryData(['hotspotOverlays', hotspotId], context?.previousOverlays);
    },
    onSettled: async (_, error, { hotspotId }) => {
      if (error) {
        console.error('Error uploading image:', error);
      }
      queryClient.invalidateQueries({ queryKey: ['hotspotOverlays', hotspotId] });
      queryClient.invalidateQueries({ queryKey: ['hotspotOverlayGroups', hotspotId] });
      queryClient.invalidateQueries({ queryKey: ['hotspotObjects', hotspotId] });
      queryClient.invalidateQueries({ queryKey: ['photoHotspots'] });

      // Invalidate layout hotspots for immediate reflection in hotspots table
      invalidateLayoutHotspots(queryClient);
    },
  });
};

export const useUpdateOverlay = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      hotspotId,
      overlayId,
      data,
    }: {
      hotspotId: number;
      overlayId: number;
      data: { title?: string; photo?: File; group_id?: number | null; new_group_name?: string };
    }) => updateOverlay(hotspotId, overlayId, data),
    onMutate: async ({ hotspotId, overlayId, data }) => {
      await queryClient.cancelQueries({ queryKey: ['hotspotOverlays', hotspotId] });

      const previousOverlays = queryClient.getQueryData(['hotspotOverlays', hotspotId]);

      // Optimistically update the overlay
      queryClient.setQueryData(
        ['hotspotOverlays', hotspotId],
        (old: HotspotOverlaysResponse | undefined) => {
          if (!old?.overlays) return old;
          const updatedOverlays = old.overlays.map((overlay) => {
            if (overlay.id === overlayId) {
              const updated = { ...overlay };
              if (data.title !== undefined) {
                updated.title = data.title;
              }
              if (data.photo) {
                updated.url = URL.createObjectURL(data.photo); // Temporary preview URL
              }
              return updated;
            }
            return overlay;
          });
          return { overlays: updatedOverlays };
        }
      );

      return { previousOverlays };
    },
    onError: (err, { hotspotId }, context) => {
      console.error('Error updating overlay:', err);
      queryClient.setQueryData(['hotspotOverlays', hotspotId], context?.previousOverlays);
    },
    onSettled: async (_, error, { hotspotId }) => {
      if (error) {
        console.error('Error updating overlay:', error);
      }
      queryClient.invalidateQueries({ queryKey: ['hotspotOverlays', hotspotId] });
      queryClient.invalidateQueries({ queryKey: ['hotspotOverlayGroups', hotspotId] });
      queryClient.invalidateQueries({ queryKey: ['hotspotObjects', hotspotId] });
      queryClient.invalidateQueries({ queryKey: ['photoHotspots'] });

      // Invalidate layout hotspots for immediate reflection in hotspots table
      invalidateLayoutHotspots(queryClient);
    },
  });
};

export const useDeleteNote = (projectId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (noteId: number) => {
      if (!projectId) {
        throw new Error('Project ID is required for deleting notes');
      }
      return deleteNote(noteId, projectId);
    },
    onMutate: async (noteId) => {
      // We need to find which hotspot this note belongs to
      // This is a bit tricky since we don't have the hotspot ID directly
      // We'll handle this by invalidating all notes queries after deletion
      return { noteId };
    },
    onSuccess: (_, noteId) => {
      // Invalidate all notes queries since we don't know which hotspot this note belonged to
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      queryClient.invalidateQueries({ queryKey: ['hotspotObjects'] });
      queryClient.invalidateQueries({ queryKey: ['photoHotspots'] });
      queryClient.removeQueries({ queryKey: ['note', noteId] });

      // Invalidate layout hotspots for immediate reflection in hotspots table
      invalidateLayoutHotspots(queryClient);
    },
    onError: (error) => {
      console.error('Error deleting note:', error);
    },
  });
};

export const useDeleteOverlay = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ hotspotId, overlayId }: { hotspotId: number; overlayId: number }) =>
      deleteOverlay(hotspotId, overlayId),
    onMutate: async ({ hotspotId, overlayId }) => {
      await queryClient.cancelQueries({ queryKey: ['hotspotOverlays', hotspotId] });

      const previousOverlays = queryClient.getQueryData(['hotspotOverlays', hotspotId]);

      // Optimistically remove the overlay
      queryClient.setQueryData(
        ['hotspotOverlays', hotspotId],
        (old: HotspotOverlaysResponse | undefined) => {
          if (!old?.overlays) return old;
          const updatedOverlays = old.overlays.filter((overlay) => overlay.id !== overlayId);
          return { overlays: updatedOverlays };
        }
      );

      return { previousOverlays };
    },
    onError: (err, { hotspotId }, context) => {
      console.error('Error deleting overlay:', err);
      queryClient.setQueryData(['hotspotOverlays', hotspotId], context?.previousOverlays);
    },
    onSettled: async (_, error, { hotspotId }) => {
      if (error) {
        console.error('Error deleting overlay:', error);
      }
      queryClient.invalidateQueries({ queryKey: ['hotspotOverlays', hotspotId] });
      queryClient.invalidateQueries({ queryKey: ['hotspotOverlayGroups', hotspotId] });
      queryClient.invalidateQueries({ queryKey: ['hotspotObjects', hotspotId] });
      queryClient.invalidateQueries({ queryKey: ['photoHotspots'] });

      // Invalidate layout hotspots for immediate reflection in hotspots table
      invalidateLayoutHotspots(queryClient);
    },
  });
};

export const useDeleteHotspot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ hotspotId, projectId }: { hotspotId: number; projectId: string }) =>
      deleteHotspot(hotspotId, projectId),
    onMutate: async ({ hotspotId }) => {
      // Cancel any outgoing refetches for related queries
      await queryClient.cancelQueries({ queryKey: ['photoHotspots'] });
      await queryClient.cancelQueries({ queryKey: ['layoutHotspots'] });

      // Snapshot previous values for all photoHotspots queries
      const previousPhotoHotspots = queryClient.getQueriesData({ queryKey: ['photoHotspots'] });
      const previousLayoutHotspots = queryClient.getQueriesData({ queryKey: ['layoutHotspots'] });

      // Optimistically remove the hotspot from all photoHotspots caches
      queryClient.setQueriesData(
        { queryKey: ['photoHotspots'] },
        (old: PhotoHotspotsResponse | undefined) => {
          if (!old?.hotspots) return old;
          const updatedHotspots = old.hotspots.filter((hotspot) => hotspot.id !== hotspotId);
          return { hotspots: updatedHotspots };
        }
      );

      // Optimistically remove the hotspot from layout hotspots caches
      queryClient.setQueriesData({ queryKey: ['layoutHotspots'] }, (old: unknown) => {
        if (!old || typeof old !== 'object' || !('hotspots' in old)) return old;
        const oldData = old as { hotspots: Array<{ id: number; [key: string]: unknown }> };
        return {
          ...oldData,
          hotspots: oldData.hotspots.filter((hotspot) => hotspot.id !== hotspotId),
        };
      });

      return { previousPhotoHotspots, previousLayoutHotspots };
    },
    onError: (err, _variables, context) => {
      console.error('Error deleting hotspot:', err);

      // Rollback optimistic updates on error
      if (context?.previousPhotoHotspots) {
        context.previousPhotoHotspots.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousLayoutHotspots) {
        context.previousLayoutHotspots.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: async (_, error, { hotspotId }) => {
      if (error) {
        console.error('Error deleting hotspot:', error);
        // Invalidate all related queries on error to ensure consistency
        queryClient.invalidateQueries({ queryKey: ['photoHotspots'] });
        queryClient.invalidateQueries({ queryKey: ['layoutHotspots'] });
      }

      // Remove specific hotspot-related queries
      queryClient.removeQueries({ queryKey: ['hotspotObjects', hotspotId] });
      queryClient.removeQueries({ queryKey: ['hotspotOverlays', hotspotId] });
      queryClient.removeQueries({ queryKey: ['notes', hotspotId] });
      queryClient.removeQueries({ queryKey: ['maintenanceRequests', hotspotId] });

      // Invalidate layout hotspots for immediate reflection in hotspots table
      invalidateLayoutHotspots(queryClient);
    },
  });
};
